// src/config/role.js

export const ROLES = {
  APPROVER: "APPROVER",
  CREATOR: "CREATOR",
  DOER: "DOER",
  VIEWER: "VIEWER",
  CLIENT: "CLIENT",
};

export const PERM = {
  PROJECT_VIEW: "PROJECT_VIEW",
  PROJECT_UPDATE: "PROJECT_UPDATE",
  PROJECT_DELETE: "PROJECT_DELETE",
  PROJECT_REMOVE_MEMBER: "PROJECT_REMOVE_MEMBER",

  TASK_CREATE: "TASK_CREATE",
  TASK_VIEW: "TASK_VIEW",
  TASK_UPDATE: "TASK_UPDATE",
  TASK_DELETE: "TASK_DELETE",
  TASK_ASSIGN: "TASK_ASSIGN",

  TODO_CREATE: "TODO_CREATE",
  TODO_VIEW: "TODO_VIEW",
  TODO_UPDATE: "TODO_UPDATE",
  TODO_DELETE: "TODO_DELETE",
  TODO_ASSIGN: "TODO_ASSIGN",

  TODOLIST_CREATE: "TODOLIST_CREATE",
  TODOLIST_VIEW: "TODOLIST_VIEW",
  TODOLIST_UPDATE: "TODOLIST_UPDATE",
  TODOLIST_DELETE: "TODOLIST_DELETE",

  // UI helper for showing assign buttons in general sections
  SEE_ASSIGN_UI: "SEE_ASSIGN_UI",
};
// Who can assign to whom, in which context (todo|task)
// src/config/role.js

export const isForbiddenAssignment = (assignerRoleLike, targetRoleLike, context) => {
  const assigner = normalizeRole(assignerRoleLike);
  const target = normalizeRole(targetRoleLike);
  if (!assigner || !target) return false;

  // NEW GLOBAL RULE: never assign tasks/todos to CLIENT or VIEWER
  if (target === ROLES.CLIENT || target === ROLES.VIEWER) {
    return true;
  }

  // NEW: For ToDo assignments, DOER can assign ONLY to DOER
  if (context === "todo") {
    if (assigner === ROLES.DOER && target !== ROLES.DOER) {
      return true;
    }
  }

  // Existing rule: For Task assignments, CREATOR cannot assign to APPROVER
  if (context === "task") {
    if (assigner === ROLES.CREATOR && target === ROLES.APPROVER) {
      return true;
    }
  }

  return false;
};



// Map backend roleType/roleId to canonical names
export const normalizeRole = (roleLike) => {
  if (!roleLike) return null;
  if (typeof roleLike === "number") {
    if (roleLike === 1) return ROLES.CREATOR;
    if (roleLike === 2) return ROLES.APPROVER;
    if (roleLike === 3) return ROLES.DOER;
    if (roleLike === 4) return ROLES.VIEWER;
    return null;
  }
  const up = String(roleLike).trim().toUpperCase();
  if (up.includes("APPROVER")) return ROLES.APPROVER;
  if (up.includes("CREATOR")) return ROLES.CREATOR;
  if (up.includes("DOER")) return ROLES.DOER;
  if (up.includes("VIEWER")) return ROLES.VIEWER;
  if (up.includes("CLIENT")) return ROLES.CLIENT;
  return null;
};

// Role → permissions table (CREATOR can assign to others)
export const ROLE_PERMISSIONS = {
  [ROLES.APPROVER]: new Set([
    PERM.PROJECT_VIEW, PERM.PROJECT_UPDATE, PERM.PROJECT_DELETE, PERM.PROJECT_REMOVE_MEMBER,
    PERM.TASK_CREATE, PERM.TASK_VIEW, PERM.TASK_UPDATE, PERM.TASK_DELETE, PERM.TASK_ASSIGN,
    PERM.TODO_CREATE, PERM.TODO_VIEW, PERM.TODO_UPDATE, PERM.TODO_DELETE, PERM.TODO_ASSIGN,
    PERM.TODOLIST_CREATE, PERM.TODOLIST_VIEW, PERM.TODOLIST_UPDATE, PERM.TODOLIST_DELETE,
    PERM.SEE_ASSIGN_UI,
  ]),
  [ROLES.CREATOR]: new Set([
    PERM.PROJECT_VIEW, PERM.PROJECT_UPDATE,
    PERM.TASK_CREATE, PERM.TASK_VIEW, PERM.TASK_UPDATE, PERM.TASK_DELETE, PERM.TASK_ASSIGN,
    PERM.TODO_CREATE, PERM.TODO_VIEW, PERM.TODO_UPDATE, PERM.TODO_DELETE, PERM.TODO_ASSIGN,
    PERM.TODOLIST_CREATE, PERM.TODOLIST_VIEW, PERM.TODOLIST_UPDATE, PERM.TODOLIST_DELETE,
    PERM.SEE_ASSIGN_UI,
  ]),
  [ROLES.DOER]: new Set([
    PERM.PROJECT_VIEW,
    PERM.TASK_CREATE, PERM.TASK_VIEW, PERM.TASK_UPDATE, PERM.TASK_DELETE,
    PERM.TODO_CREATE, PERM.TODO_VIEW, PERM.TODO_UPDATE, PERM.TODO_DELETE,
    PERM.TODOLIST_CREATE, PERM.TODOLIST_VIEW, PERM.TODOLIST_UPDATE, PERM.TODOLIST_DELETE,
  ]),
  [ROLES.VIEWER]: new Set([
    PERM.PROJECT_VIEW, PERM.TASK_VIEW, PERM.TODO_VIEW, PERM.TODOLIST_VIEW,
  ]),
  [ROLES.CLIENT]: new Set([
    PERM.PROJECT_VIEW, PERM.TASK_VIEW, PERM.TODO_VIEW, PERM.TODOLIST_VIEW,
  ]),
};

export const can = (roleLike, permission) => {
  const role = normalizeRole(roleLike);
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
};

// Helpful policy helpers you can reuse anywhere
export const defaultTodoAssignee = (roleLike, creatorEmpId) =>
  can(roleLike, PERM.TODO_ASSIGN) ? null : creatorEmpId ?? null;

export const canSeeTaskAssignUI = (roleLike) => can(roleLike, PERM.SEE_ASSIGN_UI);
export const canAssignTask = (roleLike) => can(roleLike, PERM.TASK_ASSIGN);
export const canAssignTodo = (roleLike) => can(roleLike, PERM.TODO_ASSIGN);
